import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Sun, Thermometer, Coffee, Users, Search, ChevronRight,
  Loader2, AlertTriangle, RefreshCw, CalendarDays, MapPin, Building2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TYPE_META = {
  vacation: {
    label: "Vacation",
    short: "VAC",
    Icon: Sun,
    cls: "bg-amber-100 text-amber-800 border-amber-200",
  },
  sick: {
    label: "Sick",
    short: "SICK",
    Icon: Thermometer,
    cls: "bg-red-100 text-red-800 border-red-200",
  },
  day_off: {
    label: "Day Off",
    short: "DOF",
    Icon: Coffee,
    cls: "bg-indigo-100 text-indigo-800 border-indigo-200",
  },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { label: type, short: type, Icon: CalendarDays, cls: "bg-gray-100 text-gray-700 border-gray-200" };
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${meta.cls}`}
      data-testid={`vacation-type-badge-${type}`}
    >
      <Icon className="w-3 h-3" /> {meta.short}
    </span>
  );
}

/** Short calendar-style date label (Mon Jul 14). */
function fmtDay(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-CA", {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function EntryRow({ entry }) {
  const single = entry.start_day === entry.end_day;
  return (
    <div
      className="flex items-start justify-between gap-3 px-3 py-2 border-b border-summit-hairline last:border-0 hover:bg-summit-glacier-soft/40"
      data-testid={`vacation-entry-${entry.session_ids?.[0] || entry.user?.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{entry.user?.display_name}</span>
          <TypeBadge type={entry.type} />
          {entry.is_active_today && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border-emerald-200">
              Off today
            </span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>#{entry.user?.employee_number || "—"}</span>
          {entry.user?.job_title && <span>· {entry.user.job_title}</span>}
          {entry.notes && <span className="normal-case tracking-normal text-gray-400 truncate max-w-xs">· “{entry.notes}”</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs font-mono">
          {single ? fmtDay(entry.start_day) : `${fmtDay(entry.start_day)} → ${fmtDay(entry.end_day)}`}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">
          {entry.days} day{entry.days === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function CollapsibleShell({ title, count, testid, children, defaultOpen = true, level = 0 }) {
  const [open, setOpen] = useState(defaultOpen);
  const indent = { 0: "", 1: "ml-4", 2: "ml-8" }[level] || "";
  const border = {
    0: "border-summit-hairline",
    1: "border-summit-hairline/70",
    2: "border-summit-hairline/50",
  }[level] || "border-summit-hairline";
  return (
    <div className={`border ${border} rounded-md bg-white ${indent}`} data-testid={testid}>
      <div
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        className="w-full px-3 py-2 flex items-center justify-between cursor-pointer select-none hover:bg-summit-glacier-soft/60"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-90" : "rotate-0"}`} />
          {title}
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest bg-summit-glacier text-summit-ink border-summit-hairline shrink-0">
          <Users className="w-3 h-3" /> {count}
        </span>
      </div>
      {open && <div className="border-t border-summit-hairline">{children}</div>}
    </div>
  );
}

function DepartmentGroup({ dep }) {
  return (
    <CollapsibleShell
      testid={`vacation-dep-${dep.department}`}
      level={0}
      title={
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-summit-primary" />
          <span className="font-medium text-sm truncate">{dep.department}</span>
        </div>
      }
      count={dep.entry_count}
    >
      <div className="p-2 space-y-2">
        {dep.locations.map((loc) => (
          <LocationGroup key={`${dep.department}-${loc.location}`} loc={loc} depName={dep.department} />
        ))}
      </div>
    </CollapsibleShell>
  );
}

function LocationGroup({ loc, depName }) {
  return (
    <CollapsibleShell
      testid={`vacation-loc-${depName}-${loc.location}`}
      level={1}
      title={
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-3.5 h-3.5 text-gray-500" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-gray-600 truncate">
            {loc.location_label || loc.location}
          </span>
        </div>
      }
      count={loc.entry_count}
    >
      <div className="p-2 space-y-2">
        {loc.cities.map((cty) => (
          <CityGroup key={`${depName}-${loc.location}-${cty.city}`} cty={cty} loc={loc} depName={depName} />
        ))}
      </div>
    </CollapsibleShell>
  );
}

function CityGroup({ cty, loc, depName }) {
  return (
    <CollapsibleShell
      testid={`vacation-city-${depName}-${loc.location}-${cty.city}`}
      level={2}
      title={
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] text-gray-600 truncate">{cty.city}</span>
        </div>
      }
      count={cty.entry_count}
    >
      <div>
        {cty.entries.map((e) => (
          <EntryRow key={`${e.user.id}-${e.type}-${e.start_day}`} entry={e} />
        ))}
      </div>
    </CollapsibleShell>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
function isoAddDays(iso, delta) {
  const d = new Date(iso + "T12:00:00");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function OnVacation() {
  const { user } = useAuth();
  const [data, setData] = useState({ start_date: null, end_date: null, entries: [], groups: [], scope: null });
  const [range, setRange] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [payPeriodLabel, setPayPeriodLabel] = useState(null);

  const canSeeAll = data.scope === "all";

  const load = async (startISO, endISO) => {
    setLoading(true);
    try {
      const params = {};
      if (startISO) params.start_date = startISO;
      if (endISO) params.end_date = endISO;
      const { data: resp } = await api.get("/team/on-vacation", { params });
      setData(resp);
      if (!startISO || !endISO) {
        setRange({ start: resp.start_date, end: resp.end_date });
      }
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load vacation list";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Kick off with the server's default (current pay period).
    load();
    // Also fetch current pay-period metadata to show a friendly label under
    // the range picker.
    api.get("/pay-periods/current").then(({ data: pp }) => {
      setPayPeriodLabel(`Pay period ${pp.period_number} · ${pp.range_label}`);
    }).catch(() => setPayPeriodLabel(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = () => {
    if (!range.start || !range.end) return;
    load(range.start, range.end);
  };

  const jumpToday = () => {
    const t = todayISO();
    setRange({ start: t, end: t });
    load(t, t);
  };

  const jumpPayPeriod = () => {
    // Clear params → server resolves current pay period.
    setRange({ start: null, end: null });
    load();
  };

  const jumpNext30 = () => {
    const t = todayISO();
    const e = isoAddDays(t, 29);
    setRange({ start: t, end: e });
    load(t, e);
  };

  // -------------------------------------------------------------------------
  // Filtering — search + type filter. We rebuild the grouped tree from the
  // filtered entries so counts stay accurate.
  // -------------------------------------------------------------------------
  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data.entries || []).filter((e) => {
      if (typeFilter !== "all" && e.type !== typeFilter) return false;
      if (!q) return true;
      return (
        (e.user?.display_name || "").toLowerCase().includes(q) ||
        (e.user?.employee_number || "").toString().toLowerCase().includes(q) ||
        (e.user?.department || "").toLowerCase().includes(q) ||
        (e.user?.city_name || "").toLowerCase().includes(q) ||
        (e.user?.location || "").toLowerCase().includes(q) ||
        (e.notes || "").toLowerCase().includes(q)
      );
    });
  }, [data.entries, query, typeFilter]);

  const filteredGroups = useMemo(() => {
    // Rebuild dept → location → city tree from filtered entries for accurate counts.
    const groups = {};
    for (const e of filteredEntries) {
      const dep = e.user?.department || "—";
      const loc = e.user?.location || "—";
      const cty = e.user?.city_name || e.user?.city_code || "—";
      const depBucket = groups[dep] || (groups[dep] = { department: dep, locations: {}, entry_count: 0 });
      const locBucket = depBucket.locations[loc] || (depBucket.locations[loc] = {
        location: loc, location_label: e.user?.location_label || loc, cities: {}, entry_count: 0,
      });
      const ctyBucket = locBucket.cities[cty] || (locBucket.cities[cty] = { city: cty, entries: [], entry_count: 0 });
      ctyBucket.entries.push(e);
      ctyBucket.entry_count += 1;
      locBucket.entry_count += 1;
      depBucket.entry_count += 1;
    }
    return Object.values(groups)
      .sort((a, b) => a.department.localeCompare(b.department))
      .map((dep) => ({
        ...dep,
        locations: Object.values(dep.locations)
          .sort((a, b) => a.location.localeCompare(b.location))
          .map((loc) => ({
            ...loc,
            cities: Object.values(loc.cities)
              .sort((a, b) => a.city.localeCompare(b.city))
              .map((cty) => ({
                ...cty,
                entries: cty.entries.sort((a, b) =>
                  a.user.display_name.localeCompare(b.user.display_name) ||
                  a.start_day.localeCompare(b.start_day)
                ),
              })),
          })),
      }));
  }, [filteredEntries]);

  const counts = useMemo(() => {
    const c = { total: filteredEntries.length, vacation: 0, sick: 0, day_off: 0, off_today: 0 };
    for (const e of filteredEntries) {
      if (e.type === "vacation") c.vacation += 1;
      else if (e.type === "sick") c.sick += 1;
      else if (e.type === "day_off") c.day_off += 1;
      if (e.is_active_today) c.off_today += 1;
    }
    return c;
  }, [filteredEntries]);

  const distinctUsers = useMemo(() => new Set(filteredEntries.map((e) => e.user?.id)).size, [filteredEntries]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="on-vacation-page">
      <div className="flex items-center gap-3 mb-2">
        <Link
          to="/team"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-summit-primary"
          data-testid="on-vacation-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back to team
        </Link>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight" data-testid="on-vacation-page-title">
            On vacation
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            {canSeeAll
              ? "Everyone across the company scheduled for Vacation, Sick, or Day-Off in the selected window — grouped by department, location, and city."
              : `Everyone in your department (${user?.department || "—"}) scheduled for Vacation, Sick, or Day-Off in the selected window.`}
          </p>
          {payPeriodLabel && (
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-1" data-testid="on-vacation-pp-label">
              Default view: {payPeriodLabel}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(range.start, range.end)}
          disabled={loading}
          className="h-9 border-summit-hairline text-summit-primary hover:bg-summit-glacier"
          data-testid="on-vacation-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5 text-xs">Refresh</span>
        </Button>
      </div>

      {/* --- Filter bar --- */}
      <div className="mt-6 border border-summit-hairline rounded-lg bg-white p-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Start</div>
          <Input
            type="date"
            value={range.start || ""}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            className="h-9 w-40 border-summit-hairline text-xs"
            data-testid="on-vacation-start"
          />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">End</div>
          <Input
            type="date"
            value={range.end || ""}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            className="h-9 w-40 border-summit-hairline text-xs"
            data-testid="on-vacation-end"
          />
        </div>
        <Button
          size="sm"
          onClick={applyRange}
          disabled={loading || !range.start || !range.end}
          className="h-9 bg-summit-primary hover:bg-summit-primary-hover text-white"
          data-testid="on-vacation-apply"
        >
          Apply
        </Button>
        <div className="w-px h-8 bg-summit-hairline mx-1" />
        <Button size="sm" variant="outline" onClick={jumpToday} className="h-9 border-summit-hairline text-xs" data-testid="on-vacation-today">Today</Button>
        <Button size="sm" variant="outline" onClick={jumpPayPeriod} className="h-9 border-summit-hairline text-xs" data-testid="on-vacation-payperiod">Pay period</Button>
        <Button size="sm" variant="outline" onClick={jumpNext30} className="h-9 border-summit-hairline text-xs" data-testid="on-vacation-next30">Next 30d</Button>

        <div className="ml-auto flex items-end gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Type</div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 rounded-md border border-summit-hairline px-2 text-xs bg-white"
              data-testid="on-vacation-type-filter"
            >
              <option value="all">All time-off</option>
              <option value="vacation">Vacation</option>
              <option value="sick">Sick</option>
              <option value="day_off">Day Off</option>
            </select>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Search</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, emp #, notes…"
                className="h-9 pl-8 pr-3 border-summit-hairline text-xs w-64"
                data-testid="on-vacation-search"
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- Stat chips --- */}
      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="on-vacation-stats">
        <StatChip label="Total" value={counts.total} testid="on-vacation-count-total" />
        <StatChip label="Vacation" value={counts.vacation} tone="amber" testid="on-vacation-count-vacation" />
        <StatChip label="Sick" value={counts.sick} tone="red" testid="on-vacation-count-sick" />
        <StatChip label="Day Off" value={counts.day_off} tone="indigo" testid="on-vacation-count-day-off" />
        <StatChip label="Off today" value={counts.off_today} tone="emerald" testid="on-vacation-count-today" />
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
          <Users className="w-3 h-3" /> {distinctUsers} user{distinctUsers === 1 ? "" : "s"}
        </span>
      </div>

      {/* --- Body --- */}
      {error && (
        <div className="mt-5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="on-vacation-error">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}

      {loading && filteredEntries.length === 0 ? (
        <div className="py-16 text-center text-xs text-gray-400" data-testid="on-vacation-loading">
          <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-400" data-testid="on-vacation-empty">
          No one is scheduled to be off in this window.
        </div>
      ) : (
        <div className="mt-5 space-y-3" data-testid="on-vacation-groups">
          {filteredGroups.map((dep) => (
            <DepartmentGroup key={dep.department} dep={dep} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, tone, testid }) {
  const toneMap = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const cls = toneMap[tone] || "bg-summit-glacier text-summit-ink border-summit-hairline";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border font-mono text-[10px] uppercase tracking-widest ${cls}`}
      data-testid={testid}
    >
      {label}
      <span className="tabular-nums font-semibold">{value}</span>
    </span>
  );
}
